// @givmo/board — whether the Board Asks are served (EMC-WEB4). While the backend withholds them (Board
// or Fundraisers dark, or Board not offered to the organization; the ask has had no switch of its own
// since EMC-BE21) the list read is the absent-route 404: this read RESOLVES that to `null`, a settled
// answer meaning "not served", so the sidebar leaves the row out and nothing retries. There is no web
// flag: the backend's answer is the gate, as the self-assessment's is.

import { useQuery } from '@tanstack/react-query';
import { boardAskKeys, fetchBoardAsks, type BoardAskSummary } from '@givmo/org-console';
import { hasBoardPermission } from '../../board-host';
import { BOARD_READ_PERMISSION } from '../../board-routes';
import { useBoardStanding } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';

/**
 * Whether the row is drawn: `undefined` until a holder's read has answered, as every counted row
 * waits, so a gated module (off, locked, setting up) reads nothing of Board.
 */
export function useBoardAsksServed(): boolean | undefined {
  const { orgRef, permissions } = useBoardStanding();
  const allowed = hasBoardPermission(permissions, BOARD_READ_PERMISSION);
  const read = useQuery({
    queryKey: [...boardAskKeys.list(orgRef ?? ''), 'served'],
    enabled: allowed && orgRef !== null,
    retry: (failures, error) => !isNotServed(error) && failures < 1,
    queryFn: async ({ signal }): Promise<BoardAskSummary[] | null> => {
      try {
        return await fetchBoardAsks(orgRef ?? '', signal);
      } catch (error) {
        if (isNotServed(error)) return null;
        throw error;
      }
    },
  });
  if (!allowed) return undefined;
  return read.data === undefined ? undefined : read.data !== null;
}
